import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Signup = () => {
  const [formData, setFormData] = useState({ firstName: '', lastName: '', username: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const { register } = useAuth();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const result = await register(formData);
      if (result.success) {
        setSuccess('Account created successfully! Redirecting to sign in...');
        setTimeout(() => navigate('/login'), 2000);
      } else { setError(result.error || 'Registration failed'); }
    } catch { setError('An unexpected error occurred'); } finally { setLoading(false); }
  };

  const benefits = [
    { icon: 'rocket_launch', title: 'Deploy in minutes', desc: 'Your first workflow live in under 10 minutes' },
    { icon: 'verified_user', title: 'Enterprise security', desc: 'SOC 2 Type II certified, AES-256 encryption' },
    { icon: 'trending_up', title: 'Compound growth', desc: 'Every automation compounds your competitive advantage' },
  ];

  return (
    <div className="min-h-screen flex font-display" style={{ backgroundColor: '#101c22', color: '#fff' }}>
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col justify-between p-12 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(19,164,236,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(19,164,236,0.15) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="absolute top-1/3 left-1/3 w-80 h-80 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, #13a4ec 0%, transparent 70%)', filter: 'blur(80px)', opacity: 0.2 }} />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, #7c3aed 0%, transparent 70%)', filter: 'blur(80px)', opacity: 0.15 }} />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-9 h-9" style={{ color: '#13a4ec' }}>
              <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="20" stroke="currentColor" strokeDasharray="10 4" strokeWidth="4" />
                <circle cx="24" cy="24" fill="currentColor" r="8" />
                <path d="M40 24C40 32.8366 32.8366 40 24 40" stroke="currentColor" strokeLinecap="round" strokeWidth="2" />
              </svg>
            </div>
            <span className="text-xl font-bold text-white">OrbitMind</span>
          </div>
          <h2 className="text-4xl font-extrabold text-white mb-3 leading-tight">Join 2,000+ teams<br />automating with AI</h2>
          <p className="text-gray-400 mb-12">Build, deploy, and scale AI workflows without writing a single line of code.</p>
          <div className="space-y-6">
            {benefits.map((b) => (
              <div key={b.icon} className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}>
                  <span className="material-symbols-outlined text-white text-base">{b.icon}</span>
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">{b.title}</p>
                  <p className="text-gray-400 text-sm">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 rounded-2xl border border-white/10 p-5 flex items-center gap-4" style={{ backgroundColor: 'rgba(19,164,236,0.08)' }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}>
            <span className="material-symbols-outlined text-white text-base">card_giftcard</span>
          </div>
          <div>
            <p className="font-bold text-white text-sm">Start free. No credit card required.</p>
            <p className="text-gray-400 text-xs">Full access to all features for 14 days.</p>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="w-8 h-8" style={{ color: '#13a4ec' }}>
              <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="20" stroke="currentColor" strokeDasharray="10 4" strokeWidth="4" />
                <circle cx="24" cy="24" fill="currentColor" r="8" />
                <path d="M40 24C40 32.8366 32.8366 40 24 40" stroke="currentColor" strokeLinecap="round" strokeWidth="2" />
              </svg>
            </div>
            <span className="text-xl font-bold text-white">OrbitMind</span>
          </div>
          <h1 className="text-3xl font-extrabold text-white mb-1">Create your account</h1>
          <p className="text-gray-400 text-sm mb-8">Join the AI revolution</p>
          {success && <div className="mb-5 px-4 py-3 rounded-xl border border-emerald-500/20 bg-emerald-500/10 text-emerald-400 text-sm">{success}</div>}
          {error && <div className="mb-5 px-4 py-3 rounded-xl border border-red-500/20 bg-red-500/10 text-red-400 text-sm">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">First Name</label>
                <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} disabled={loading} placeholder="John" className="w-full h-11 px-4 rounded-xl border border-white/10 bg-white/5 text-white placeholder-gray-500 outline-none focus:border-primary transition-colors text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Last Name</label>
                <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} disabled={loading} placeholder="Doe" className="w-full h-11 px-4 rounded-xl border border-white/10 bg-white/5 text-white placeholder-gray-500 outline-none focus:border-primary transition-colors text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Username</label>
              <input type="text" name="username" value={formData.username} onChange={handleChange} required disabled={loading} placeholder="johndoe" className="w-full h-11 px-4 rounded-xl border border-white/10 bg-white/5 text-white placeholder-gray-500 outline-none focus:border-primary transition-colors text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Work Email</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} required disabled={loading} placeholder="john@company.com" className="w-full h-11 px-4 rounded-xl border border-white/10 bg-white/5 text-white placeholder-gray-500 outline-none focus:border-primary transition-colors text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
              <input type="password" name="password" value={formData.password} onChange={handleChange} required minLength="6" disabled={loading} placeholder="••••••••" className="w-full h-11 px-4 rounded-xl border border-white/10 bg-white/5 text-white placeholder-gray-500 outline-none focus:border-primary transition-colors text-sm" />
              <p className="mt-1.5 text-xs text-gray-500">Minimum 6 characters</p>
            </div>
            <button type="submit" disabled={loading} className="w-full h-12 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}>
              {loading ? (<><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /><span>Creating Account...</span></>) : (<><span>Create Account</span><span className="material-symbols-outlined text-base">arrow_forward</span></>)}
            </button>
          </form>
          <p className="text-center text-sm text-gray-400 mt-8">
            Already have an account?{' '}
            <Link to="/login" className="font-bold hover:opacity-80 transition-opacity" style={{ color: '#13a4ec' }}>Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
